import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "all");
  const [sortBy, setSortBy] = useState<"latest" | "hot">("latest");

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const filteredArticles = useMemo(() => {
    let result =
      activeSlug === "all"
        ? [...articles]
        : articles.filter((a) => a.categorySlug === activeSlug);

    if (sortBy === "hot") {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort(
        (a, b) =>
          new Date(b.publishDate).getTime() -
          new Date(a.publishDate).getTime()
      );
    }
    return result;
  }, [activeSlug, sortBy]);

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: currentCategory?.name || "全部分类" },
  ];

  return (
    <>
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-3">
              {currentCategory?.name || "全部"}漫画
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              {currentCategory?.description ||
                "浏览全部打屁股漫画精选内容"}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSortBy("latest")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  sortBy === "latest"
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy("hot")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  sortBy === "hot"
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                最热
              </button>
            </div>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-500">该分类下暂无漫画内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}

          {filteredArticles.length > 0 && (
            <p className="text-center text-sm text-foreground-400 mt-8">
              共 {filteredArticles.length} 篇漫画内容
            </p>
          )}
        </div>
      </section>
    </>
  );
}